import DownloadIcon from '@/assets/icons/DownloadIcon'
import EditIcon from '@/assets/icons/EditIcon'
import {
    ColumnConfigurationIcon,
    FileExcludeIcon,
    FullScreenIcon,
} from '@/assets/icons/index.js'
import CloseIcon from '@/assets/icons/MTCancelIcon'
import SaveIcon from '@/assets/icons/MTSaveIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import { ColumnConfiguration, Search } from '@/components/common/index.js'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import type { MTKeyFilterViewData } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { MTKeyFilterPanel } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { MTWhiteTooltip } from '@/multi-territory/mt-components/mt-shared/MTWhiteTooltip'
import {
    MT_PREPARE_ACTIONS,
    MT_PREPARE_DEFAULT_TAB,
    MT_PREPARE_TABS,
    MT_PREPARE_TOOLBAR_CONFIG,
} from '@/multi-territory/mt-constants/mt-prepare-constants'
import { useMTUnsavedChangesGuard } from '@/multi-territory/mt-hooks/useMTUnsavedChangesGuard'
import type { MTPrepareInlineEdit } from '@/multi-territory/mt-services/mt-prepare-api'
import {
    fetchMTPrepareBucketCounts,
    fetchMTPrepareNegativeEntries,
    fetchMTPrepareTransactionalData,
    fetchMTRateDeterminationStatus,
    fetchMTTransactionDataMetadata,
    mtCalculateRateRequest,
    mtExclusionAction,
    mtExportPrepareData,
    mtInlineEditPrepareData,
    mtReprocessPrepare,
} from '@/multi-territory/mt-services/mt-prepare-api'
import { metadataToTableColumns } from '@/multi-territory/mt-services/mt-service-master-api'
import type {
    MTMetadataField,
    MTMetadataTableColumn,
} from '@/multi-territory/mt-types/mt-metadata'
import { buildRegexErrorMessage } from '@/multi-territory/mt-utils/mt-regex-hint'
import { formatMTHeaderLabel } from '@/multi-territory/mt-utils/mt-search-fields'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
    Box,
    Button,
    Divider,
    IconButton,
    Tab,
    Tabs,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

// ─── Error Count Cell ─────────────────────────────────────────────────────────

type ParsedError = { errorMessage: string }

/**
 * Extracts a human-readable message from a single raw error object.
 * The backend is inconsistent about the field name — some payloads use
 * `errorMessage`, others use `message` (with `field`/`failureType`, e.g.
 * DUPLICATE_RECORD). Fall back across the known variants so no error
 * renders as a blank tooltip row.
 */
function extractErrorMessage(err: any): string {
  if (err == null) return ''
  if (typeof err === 'string') return err
  return (
    err.errorMessage ??
    err.message ??
    err.errorDescription ??
    err.description ??
    ''
  )
}

function parseErrors(raw: any): ParsedError[] {
  let list: any[] = []
  if (!raw) return []
  if (Array.isArray(raw)) {
    list = raw
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      list = Array.isArray(parsed) ? parsed : [parsed]
    } catch {
      return []
    }
  } else if (typeof raw === 'object') {
    list = [raw]
  }

  return list
    .map(err => ({ errorMessage: extractErrorMessage(err) }))
    .filter(err => err.errorMessage.trim() !== '')
}

function ErrorCountCell({ errors }: { errors: any }) {
  const parsed = parseErrors(errors)
  if (parsed.length === 0) return null

  return (
    <MTWhiteTooltip
      placement="bottom-start"
      title={
        <div>
          {parsed.map((err, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                margin: '8px 0',
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: '#ef4444',
                  display: 'inline-block',
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '14px', lineHeight: 1.4 }}>
                {err.errorMessage}
              </span>
            </div>
          ))}
        </div>
      }
      arrow
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
        }}
      >
        <WarningAmberIcon style={{ color: '#E0301E' }} />
        <span>{parsed.length}</span>
      </div>
    </MTWhiteTooltip>
  )
}

// ─── Error Count Column definition ───────────────────────────────────────────

const ERROR_COUNT_COLUMN: MTMetadataTableColumn & {
  render: (v: any, row: any) => React.ReactNode
} = {
  id: 'errors',
  label: 'Error count',
  type: 'text',
  sortable: false,
  editable: false,
  minWidth: '130px',
  render: (_v: any, row: any) => <ErrorCountCell errors={row.errors} />,
}

// ─── API totals normalization ────────────────────────────────────────────────

/**
 * Normalizes the backend `columnTotals` map (keyed by column id/field_key) into
 * display-ready values for the pinned total row. Numeric totals come back as raw
 * numbers (e.g. 2963000.00, 435670.46, 1180029.5); format them like the India
 * total row — at most 2 decimals, dropping a trailing ".00" so whole numbers
 * read cleanly (70000, not 70000.00). Non-numeric values pass through as-is.
 * Returns `null` when the map is missing/empty so the caller falls back to the
 * client-side per-page aggregate.
 */
function normalizeApiTotals(
  summary: unknown
): Record<string, number | string> | null {
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) {
    return null
  }
  const out: Record<string, number | string> = {}
  for (const [key, val] of Object.entries(summary as Record<string, unknown>)) {
    if (val == null || val === '') continue
    const num =
      typeof val === 'number' ? val : Number(String(val).replace(/,/g, ''))
    if (Number.isNaN(num)) {
      out[key] = String(val)
      continue
    }
    const rounded = Math.round(num * 100) / 100
    out[key] = Number.isInteger(rounded) ? rounded : rounded.toFixed(2)
  }
  return Object.keys(out).length > 0 ? out : null
}

// ─── Types ───────────────────────────────────────────────────────────────────

type FilterData = MTKeyFilterViewData & {
  year?: string
  months?: { year: number; month: number }[]
  entityIdentifiers?: string[]
}

// ─── Toolbar Icons ────────────────────────────────────────────────────────────

// Shared style so EVERY toolbar icon (via ActionIconBtn and the inline
// Edit/Save/Cancel buttons) looks identical:
// - Same fixed padding → the grey hover square is the SAME size on every icon
//   (previously ActionIconBtn used size="small" and the edit buttons
//   size="medium", so the hover squares differed).
// - Same grey hover square as Search (instead of MUI's near-invisible default).
// - Hover still shows while DISABLED (e.g. Exclude/Include with no selection):
//   MUI sets pointer-events:none on disabled buttons, which suppresses hover,
//   so we re-enable pointer events and dim the icon instead.
const TOOLBAR_ICON_SX = {
  color: 'var(--forbrand-primary)',
  padding: '8px',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
  '&.Mui-disabled': {
    pointerEvents: 'auto',
    color: 'var(--forbrand-primary)',
    opacity: 0.38,
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
  },
} as const

const ActionIconBtn = ({
  title,
  onClick,
  icon,
  disabled = false,
}: {
  title: string
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  icon: React.ReactNode
  disabled?: boolean
}) => (
  <Tooltip title={title}>
    <span>
      <IconButton sx={TOOLBAR_ICON_SX} onClick={onClick} disabled={disabled}>
        {icon}
      </IconButton>
    </span>
  </Tooltip>
)

// ─── Main Component ───────────────────────────────────────────────────────────

const MTPrepare = () => {
  const { showSnackbar } = useSnackbar() as any

  // ── Filter / view state ──
  const [filterData, setFilterData] = useState<FilterData | null>(null)
  const [showTable, setShowTable] = useState(false)

  // ── Tab state ──
  const [selectedTab, setSelectedTab] = useState(MT_PREPARE_DEFAULT_TAB)
  const [selectedSubTab, setSelectedSubTab] = useState(1)

  // ── Table state ──
  const [data, setData] = useState<Record<string, any>[]>([])
  const [loading, setLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [totalTransactions, setTotalTransactions] = useState(0)
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({})
  // Aggregated totals for the pinned total row (India-parity). Populated from
  // the list response's summary/totals block when the backend provides it;
  // falls back to a client-side per-column sum until that API lands.
  const [apiTotals, setApiTotals] = useState<Record<
    string,
    number | string
  > | null>(null)

  // ── Column config ──
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] = useState<
    HTMLElement | null | undefined
  >(null)
  const [visibleColumns, setVisibleColumns] = useState<string[]>([])
  const [metadataColumns, setMetadataColumns] = useState<
    MTMetadataTableColumn[]
  >([])
  const [rawMetadataFields, setRawMetadataFields] = useState<MTMetadataField[]>(
    []
  )

  // ── Bulk (inline) edit state ──
  // editingData is keyed by rowId -> { fieldKey: value } so every row can be
  // edited at once (matches Payee/Service Master bulk-edit behaviour).
  const [editMode, setEditMode] = useState(false)
  const [editingData, setEditingData] = useState<Record<string, any>>({})
  // fieldErrors highlights the specific cells that failed validation (red border).
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, Record<string, boolean>>
  >({})
  const [isSaving, setIsSaving] = useState(false)

  // Unsaved-changes guard: while in edit mode, tab switches, search,
  // pagination, View (key filter) and navigating away ask for confirmation.
  // Proceed discards edits and exits edit mode.
  const { guardUnsavedChanges, unsavedChangesDialog } =
    useMTUnsavedChangesGuard(editMode, () => {
      setEditingData({})
      setFieldErrors({})
      setEditMode(false)
    })

  // ── Search ──
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: '',
      operator: 'EQUAL_TO',
      value: '',
    },
  ])

  // ── Full screen ──
  const [isFullScreen, setIsFullScreen] = useState(false)
  const handleFullscreen = () => setIsFullScreen(true)
  const handleFullScreenExit = () => setIsFullScreen(false)

  // ── Action loading ──
  const [isReprocessLoading, setIsReprocessLoading] = useState(false)
  const [isSuggestRateLoading, setIsSuggestRateLoading] = useState(false)
  const [isCooldown, setIsCooldown] = useState(false)
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Suggest WHT Rate button enablement (driven by rate-determination status) ──
  // Disabled by default until the status API confirms it can be enabled. The
  // status call runs alongside every VTD (transactional-data) list fetch.
  const [suggestRateButtonEnabled, setSuggestRateButtonEnabled] =
    useState(false)

  // ── Cross-module mutual exclusion (driven by rate-determination status) ──
  // Only one long-running operation may run at a time per entity+period:
  //  - rateDeterminationInProgress → Suggest WHT Rate is running → disable Edit + Suggest
  //  - reprocessLockInProgress     → an inline-edit/reprocess lock is held → disable Edit + Suggest
  const [rateDeterminationInProgress, setRateDeterminationInProgress] =
    useState(false)
  const [reprocessLockInProgress, setReprocessLockInProgress] = useState(false)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  // ── Derived ──
  const currentTab = MT_PREPARE_TABS[selectedTab]
  const screenName = currentTab.screenName
  const toolbarConfig =
    MT_PREPARE_TOOLBAR_CONFIG[screenName] ??
    MT_PREPARE_TOOLBAR_CONFIG['UNPROCESSED']
  // For metadata API: use sub-tab screen name (UNMAPPED/MAPPED) when on Negative Entries tab
  const metadataScreen =
    screenName === 'NEGATIVE'
      ? (currentTab.children?.find(c => c.id === selectedSubTab)?.screenName ??
        'UNMAPPED')
      : screenName

  const columns = metadataColumns

  // ── Cross-module mutual exclusion (derived) ──
  // Only one long-running operation at a time. When either is active, both the
  // Edit and Suggest WHT Rate entry points are locked until it completes.
  const anyOperationInProgress =
    rateDeterminationInProgress || reprocessLockInProgress
  // Edit is blocked while an edit/reprocess lock is held OR rate determination runs.
  const editLocked = anyOperationInProgress
  // Suggest WHT Rate is blocked while rate determination runs OR an edit lock is held.
  const suggestRateLocked = anyOperationInProgress

  const searchFields = useMemo(
    () =>
      columns.map(col => {
        // Match the advance-search label to the table header exactly
        // (sentence case / "running text", acronyms such as ID/TIN uppercase).
        const label = formatMTHeaderLabel(col.label)
        return {
          ...col,
          label,
          // Preserve the real column type (date/dropdown/number) so the advance
          // search renders the correct control (e.g. calendar for date fields).
          // Only fall back to 'text' when the metadata omits a type.
          type: col.type || 'text',
          placeholder:
            col.type === 'date'
              ? 'Select date'
              : col.type === 'dropdown'
                ? `Select ${label}`
                : `Enter ${label}`,
        }
      }) as any[],
    [columns]
  )

  useEffect(() => {
    if (columns.length > 0) setVisibleColumns(columns.map(c => c.id))
  }, [columns])

  const filteredColumns = useMemo(() => {
    const base = columns.filter(c => visibleColumns.includes(c.id))
    // Prepend Error count column only on Unprocessed tab
    if (screenName === 'UNPROCESSED') {
      return [ERROR_COUNT_COLUMN as any, ...base]
    }
    return base
  }, [columns, visibleColumns, screenName])

  // ── Total (aggregate) row ──
  // India parity: a bold totals row pinned under the header summing the amount
  // columns. Source of truth:
  //   1. apiTotals — the backend-provided summary (used as-is once that API
  //      lands; see fetchData). Keyed by column id (field_key).
  //   2. Client-side fallback — until the summary API exists, sum every column
  //      whose metadata marks it a number AND whose values parse as numbers
  //      (covers Invoice Amount, Amount subject to WHT, WHT Amount, Regross
  //      taxable income, etc.). This is page-scoped (current page rows only);
  //      the API total will be the authoritative all-pages figure.
  const amountColumnIds = useMemo(() => {
    // Treat a field as an "amount" column when the metadata types it as a
    // number, OR its validation regex is a decimal-amount pattern (the SGD
    // metadata ships amounts as type:"text" with a `^\d{1,10}(\.\d{1,2})?$`
    // regex, so type alone is not enough).
    const AMOUNT_REGEX = '^\\d{1,10}(\\.\\d{1,2})?$'
    return new Set(
      rawMetadataFields
        .filter(
          f =>
            f.type === 'number' ||
            (f.validations?.regex &&
              f.validations.regex.replace(/\//g, '') === AMOUNT_REGEX)
        )
        .map(f => f.field_key)
    )
  }, [rawMetadataFields])

  const totalsRow = useMemo<Record<string, number | string> | null>(() => {
    // Prefer the backend summary verbatim when available.
    if (apiTotals && Object.keys(apiTotals).length > 0) return apiTotals

    // No data → no total row.
    if (!data.length || amountColumnIds.size === 0) return null

    const sums: Record<string, number> = {}
    let touched = false
    for (const row of data) {
      amountColumnIds.forEach(colId => {
        const raw = row[colId]
        if (raw == null || raw === '' || raw === '-') return
        const num = Number(String(raw).replace(/,/g, ''))
        if (Number.isNaN(num)) return
        sums[colId] = (sums[colId] ?? 0) + num
        touched = true
      })
    }
    if (!touched) return null

    // Format: trim to at most 2 decimals, drop a trailing ".00" so whole
    // numbers read cleanly (e.g. 70000, not 70000.00) like the India row.
    const formatted: Record<string, number | string> = {}
    for (const [colId, val] of Object.entries(sums)) {
      const rounded = Math.round(val * 100) / 100
      formatted[colId] = Number.isInteger(rounded)
        ? rounded
        : rounded.toFixed(2)
    }
    return formatted
  }, [apiTotals, data, amountColumnIds])

  // ── Reset scroll on tab change ──
  useEffect(() => {
    if (tableContainerRef.current) {
      const tc = tableContainerRef.current.querySelector(
        '.MuiTableContainer-root'
      )
      if (tc) {
        tc.scrollLeft = 0
        tc.scrollTop = 0
      }
    }
  }, [selectedTab, selectedSubTab])

  // ── Fetch metadata columns ──
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country?.countryCode
  )

  useEffect(() => {
    if (!countryCode) return
    fetchMTTransactionDataMetadata(countryCode, metadataScreen).then(res => {
      if (res.success) {
        const fields = ((res.data as any)?.fields || []) as MTMetadataField[]
        setMetadataColumns(metadataToTableColumns(fields))
        setRawMetadataFields(fields)
      }
    })
  }, [countryCode, metadataScreen])

  // -- Fetch bucket counts --
  const fetchSummary = useCallback(
    async (fd: FilterData) => {
      if (!countryCode) return
      try {
        const payload = {
          pageFilter: null,
          searchCOList: [],
          entityIdentifiers: fd.entityIdentifiers,
          periodFilters: fd.months,
          isReportDownload: false,
        }
        const res = await fetchMTPrepareBucketCounts(payload, countryCode)
        if (res.success) {
          const list: any[] = (res.data as any)?.data || (res.data as any) || []
          const counts: Record<string, number> = {}
          let total = 0
          list.forEach((item: any) => {
            counts[item.bucket] = item.count || 0
            // Total Transactions excludes the EXCEPTIONS bucket count.
            if (item.bucket !== 'EXCEPTIONS') {
              total += item.count || 0
            }
          })
          setTabCounts(counts)
          setTotalTransactions(total)
        }
      } catch {
        /* silent */
      }
    },
    [countryCode]
  )

  // ── Fetch rate-determination status (drives Suggest WHT Rate button) ──
  // Called alongside every VTD list fetch. `buttonEnabled` from the response
  // decides whether the Suggest WHT Rate button is enabled or disabled.
  const fetchRateDeterminationStatus = useCallback(
    async (fd: FilterData, uploadCategory: string) => {
      if (!countryCode) return
      const periodFilters = (fd.months ?? []).map(
        m => `${m.year}${String(m.month).padStart(2, '0')}`
      )
      const entityTans = fd.entityIdentifiers ?? []
      if (entityTans.length === 0 || periodFilters.length === 0) {
        setSuggestRateButtonEnabled(false)
        setRateDeterminationInProgress(false)
        setReprocessLockInProgress(false)
        return
      }
      try {
        const res = await fetchMTRateDeterminationStatus(
          { entityTans, periodFilters, uploadCategory },
          countryCode
        )
        if (res.success) {
          const status = res.data as any
          setSuggestRateButtonEnabled(Boolean(status?.buttonEnabled))
          // Cross-module locks: rate determination running OR an inline-edit/
          // reprocess lock held → block the other module until it finishes.
          setRateDeterminationInProgress(
            Boolean(status?.rateDeterminationInProgress)
          )
          setReprocessLockInProgress(Boolean(status?.reprocessLockInProgress))
        } else {
          setSuggestRateButtonEnabled(false)
          setRateDeterminationInProgress(false)
          setReprocessLockInProgress(false)
        }
      } catch {
        setSuggestRateButtonEnabled(false)
        setRateDeterminationInProgress(false)
        setReprocessLockInProgress(false)
      }
    },
    [countryCode]
  )

  // ── Fetch transactional data ──
  const fetchData = useCallback(
    async ({
      page = 1,
      pageSize = itemsPerPage,
      searchList = [] as any[],
      tabScreenName = screenName,
      fd = filterData,
      subTabScreenName = '',
    } = {}) => {
      if (!fd || !countryCode) return
      setLoading(true)
      try {
        const payload = {
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize,
            sortField: 'concatenated_year_month',
            sortOrder: 'DESC',
          },
          searchCOList: searchList,
          entityIdentifiers: fd.entityIdentifiers,
          periodFilters: fd.months,
          isReportDownload: false,
          ...(subTabScreenName ? { referenceType: subTabScreenName } : {}),
        }

        let res: any
        if (tabScreenName === 'NEGATIVE') {
          res = await fetchMTPrepareNegativeEntries(payload, countryCode)
          const content =
            (res as any)?.data?.data?.content ||
            (res as any)?.data?.content ||
            []
          setData(content)
          setTotalItems(
            (res as any)?.data?.data?.totalElements ||
              (res as any)?.data?.totalElements ||
              0
          )
        } else {
          res = await fetchMTPrepareTransactionalData(
            payload,
            tabScreenName,
            countryCode
          )
          const content =
            (res as any)?.data?.data?.content ||
            (res as any)?.data?.content ||
            []
          setData(content)
          setTotalItems(
            (res as any)?.data?.data?.totalElements ||
              (res as any)?.data?.totalElements ||
              0
          )
        }
        // Pull the backend-provided aggregate totals from the response. The
        // list endpoint returns these under `columnTotals`, keyed by column id
        // (field_key, e.g. invoice_amount_in_s / wht_amount) → the authoritative
        // all-pages figure. We still probe the older summary/totals/aggregate
        // shapes as a fallback for endpoints that haven't adopted columnTotals.
        // When absent → `null` so the client-side per-page aggregate is used.
        const d = (res as any)?.data?.data ?? (res as any)?.data ?? {}
        const summary =
          d.columnTotals ?? d.summary ?? d.totals ?? d.aggregate ?? null
        setApiTotals(normalizeApiTotals(summary))
        setSelectedRows([])
        await fetchSummary(fd)
        // VTD list refreshed — refresh the Suggest WHT Rate button state too.
        await fetchRateDeterminationStatus(fd, tabScreenName)
      } catch {
        /* silent */
      } finally {
        setLoading(false)
      }
    },
    [
      filterData,
      itemsPerPage,
      screenName,
      fetchSummary,
      fetchRateDeterminationStatus,
      countryCode,
    ]
  )

  // ── Negative sub-tab effect removed — handled directly in handleTabChange / handleSubTabChange

  // ── Handlers ──

  const handleView = useCallback(
    async (fd: MTKeyFilterViewData) => {
      if (!fd.year || !fd.months || fd.months.length === 0) return
      // Build entityIdentifiers from TAN number or unique identification number
      const entityIdentifiers = fd.tanApplicable
        ? fd.tanNumber
          ? [fd.tanNumber]
          : []
        : fd.uniqueIdentificationNumber
          ? [fd.uniqueIdentificationNumber]
          : []
      const enrichedFd: FilterData = { ...fd, entityIdentifiers }
      setFilterData(enrichedFd)
      setShowTable(true)
      setCurrentPage(1)
      setSelectedRows([])
      const tab = MT_PREPARE_TABS[selectedTab]
      const subTab = tab.children?.find(c => c.id === selectedSubTab)
      await fetchData({
        page: 1,
        fd: enrichedFd,
        tabScreenName: tab.screenName,
        subTabScreenName: subTab?.screenName || '',
      })
    },
    [selectedTab, selectedSubTab, fetchData]
  )

  const switchTab = useCallback(
    async (newValue: number) => {
      const tab = MT_PREPARE_TABS[newValue]
      setSelectedTab(newValue)
      setSelectedRows([])
      setEditMode(false)
      setEditingData({})
      setFieldErrors({})
      setCurrentPage(1)
      setItemsPerPage(100)
      setSearchCriteria([
        {
          id: 1,
          logicalOperator: 'Where',
          field: '',
          operator: 'EQUAL_TO',
          value: '',
        },
      ])

      if (!filterData) return

      if (tab.screenName === 'NEGATIVE') {
        setSelectedSubTab(1)
        const defaultSubTab = tab.children?.[0]
        await fetchData({
          page: 1,
          fd: filterData,
          tabScreenName: 'NEGATIVE',
          subTabScreenName: defaultSubTab?.screenName || 'UNMAPPED',
        })
        return
      }

      await fetchData({
        page: 1,
        tabScreenName: tab.screenName,
        fd: filterData,
      })
    },
    [filterData, fetchData]
  )

  // Tab / sub-tab switching while editing asks to discard unsaved edits.
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) =>
    guardUnsavedChanges(() => {
      switchTab(newValue)
    })

  const handleSubTabChange = (id: number) => {
    if (id === selectedSubTab) return
    guardUnsavedChanges(() => {
      switchSubTab(id)
    })
  }

  const switchSubTab = async (id: number) => {
    setSelectedSubTab(id)
    setSelectedRows([])
    setCurrentPage(1)
    if (!filterData) return
    const subTab = currentTab.children?.find(c => c.id === id)
    await fetchData({
      page: 1,
      fd: filterData,
      tabScreenName: 'NEGATIVE',
      subTabScreenName: subTab?.screenName || '',
    })
  }

  const handleSelectItem = useCallback((id: string) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    )
  }, [])

  const handleSelectAll = () => {
    if (selectedRows.length === data.length) setSelectedRows([])
    else
      setSelectedRows(
        data.map(
          (r: any) => r.uuid || r.vendorTransactionalDataUuid || r.id || ''
        )
      )
  }

  const handlePageChange = (page: number) =>
    guardUnsavedChanges(() => {
      setCurrentPage(page)
      const subTab = currentTab.children?.find(c => c.id === selectedSubTab)
      fetchData({ page, subTabScreenName: subTab?.screenName || '' })
    })

  const handleItemsPerPageChange = (size: number) =>
    guardUnsavedChanges(() => {
      setItemsPerPage(size)
      setCurrentPage(1)
      const subTab = currentTab.children?.find(c => c.id === selectedSubTab)
      fetchData({
        page: 1,
        pageSize: size,
        subTabScreenName: subTab?.screenName || '',
      })
    })

  const handleSearch = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget
    guardUnsavedChanges(() => {
      setSearchOpen(v => !v)
      setAnchorEl(target)
    })
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      { id: 1, logicalOperator: 'Where', field: '', value: '' },
    ])
  }

  const handleApplySearch = async (searchList: any[]) => {
    const mapped = searchList.map((item: any, idx: number) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mapped)
    setCurrentPage(1)
    const subTab = currentTab.children?.find(c => c.id === selectedSubTab)
    await fetchData({
      page: 1,
      searchList,
      subTabScreenName: subTab?.screenName || '',
    })
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: '',
        operator: 'EQUAL_TO',
        value: '',
      },
    ])
    setCurrentPage(1)
    const subTab = currentTab.children?.find(c => c.id === selectedSubTab)
    await fetchData({
      page: 1,
      searchList: [],
      subTabScreenName: subTab?.screenName || '',
    })
    setSearchOpen(false)
  }

  const handleColumnConfigClick = (e: React.MouseEvent<HTMLElement>) => {
    setColumnConfigAnchor(e.currentTarget)
    setColumnConfigOpen(v => !v)
  }

  const handleColumnConfigClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleExcludeInclude = async () => {
    if (selectedRows.length === 0 || !countryCode) return
    const bucket =
      screenName === 'PROCESSED'
        ? MT_PREPARE_ACTIONS.EXCLUDE
        : MT_PREPARE_ACTIONS.INCLUDE
    try {
      const res = await mtExclusionAction(selectedRows, bucket, countryCode)
      if ((res as any).success) {
        showSnackbar(`${bucket} successful`, 'success')
        await fetchData({ page: currentPage })
      } else {
        showSnackbar((res as any).error || 'Action failed', 'error')
      }
    } catch {
      showSnackbar('Action failed', 'error')
    }
  }

  // ── Bulk (inline) edit handlers ──

  const getRowId = (row: any): string =>
    row.uuid ||
    row.vendorTransactionalDataUuid ||
    row.associated_uuid ||
    row.global_uuid ||
    row.id ||
    ''

  const handleEditClick = () => {
    // Guard: another operation (rate determination / edit-reprocess lock) is
    // running — do not allow a second edit to start.
    if (editLocked) {
      showSnackbar(
        'Another operation is in progress. Please wait until it completes.',
        'info'
      )
      return
    }
    // Enter edit mode immediately.
    setEditingData({})
    setFieldErrors({})
    setSelectedRows([])
    setEditMode(true)
  }

  const handleCancelEdit = () => {
    // Exit edit mode immediately.
    setEditingData({})
    setFieldErrors({})
    setEditMode(false)
  }

  // Stable identity (only functional setters used) so memoized rows aren't
  // invalidated on every parent render — critical for smooth bulk editing.
  const handleFieldChange = useCallback(
    (rowId: string, fieldKey: string, value: string) => {
      setEditingData(prev => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), [fieldKey]: value },
      }))
      // Clear the red border on this cell as soon as the user edits it.
      setFieldErrors(prev => {
        if (!prev[rowId]?.[fieldKey]) return prev
        const next = { ...prev, [rowId]: { ...prev[rowId] } }
        delete next[rowId][fieldKey]
        if (Object.keys(next[rowId]).length === 0) delete next[rowId]
        return next
      })
    },
    []
  )

  /**
   * Validate a single field value against its metadata rules.
   * Returns an error message string, or null when valid.
   */
  const validateField = (
    field: MTMetadataField,
    rawValue: any
  ): string | null => {
    const value = String(rawValue ?? '').trim()
    if (field.required && (!value || value === '-')) {
      return field.validations?.required_message || `${field.label} is required`
    }
    if (!value || value === '-') return null

    if (field.type === 'number') {
      const numVal = parseFloat(value)
      if (isNaN(numVal)) return `${field.label} must be a valid number`
      if (
        field.validations?.min !== undefined &&
        numVal < field.validations.min
      ) {
        return `${field.label} must be at least ${field.validations.min}`
      }
      if (
        field.validations?.max !== undefined &&
        numVal > field.validations.max
      ) {
        return `${field.label} must not exceed ${field.validations.max}`
      }
    }
    if (
      field.validations?.maxLength &&
      value.length > field.validations.maxLength
    ) {
      return `${field.label} must be ${field.validations.maxLength} characters or fewer (currently ${value.length})`
    }
    if (
      field.validations?.regex &&
      field.type !== 'dropdown' &&
      field.type !== 'date'
    ) {
      try {
        if (!new RegExp(field.validations.regex).test(value)) {
          // Human-readable message that also tells the user the expected format
          // (e.g. "Email is invalid. Please enter a valid email address
          // (e.g. name@example.com)").
          return buildRegexErrorMessage(
            field.label,
            field.validations.regex,
            (field.validations as any).regex_message
          )
        }
      } catch {
        /* skip malformed regex */
      }
    }
    return null
  }

  /**
   * Validate all edited rows column-by-column (left-to-right, in metadata order).
   * Returns:
   *  - errors: every failing cell (rowId -> fieldKey -> true) so they all get a red border
   *  - firstMessage: the message for the FIRST failing cell in column order — this is the
   *    single snackbar shown. After the user fixes it and clicks Save again, the next
   *    column's error surfaces (AC2: one error at a time, left to right).
   */
  const validateEdits = (): {
    errors: Record<string, Record<string, boolean>>
    firstMessage: string | null
  } => {
    const errors: Record<string, Record<string, boolean>> = {}
    let firstMessage: string | null = null

    // Editable fields ordered as the columns appear (left → right).
    const orderedFields = [...rawMetadataFields]
      .filter(f => f.editable && f.visible && !f.system_generated)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    // Iterate column-first so the earliest column's error wins the snackbar.
    for (const field of orderedFields) {
      for (const [rowId, changes] of Object.entries(editingData)) {
        const originalRow = data.find(r => getRowId(r) === rowId)
        if (!originalRow) continue
        const merged = { ...originalRow, ...(changes as Record<string, any>) }
        const msg = validateField(field, merged[field.field_key])
        if (msg) {
          if (!errors[rowId]) errors[rowId] = {}
          errors[rowId][field.field_key] = true
          if (!firstMessage) {
            const rowIndex = data.findIndex(r => getRowId(r) === rowId)
            firstMessage = `Row ${rowIndex + 1}: ${msg}`
          }
        }
      }
    }
    return { errors, firstMessage }
  }

  const handleSaveEdit = async () => {
    if (isSaving) return
    const entries = Object.entries(editingData || {})
    if (entries.length === 0) {
      showSnackbar('No changes to save', 'warning')
      return
    }

    // Validate left-to-right; show only the first error and highlight failing cells.
    const { errors, firstMessage } = validateEdits()
    setFieldErrors(errors)
    if (firstMessage) {
      showSnackbar(firstMessage, 'error')
      return
    }

    // Build inline-edit payload: only changed cells per row.
    const edits: MTPrepareInlineEdit[] = entries
      .filter(([rowId]) => rowId && !rowId.startsWith('row-'))
      .map(([uuid, changes]) => ({
        uuid,
        changedCells: changes as Record<string, string | number | null>,
      }))

    if (edits.length === 0) {
      showSnackbar('No valid rows to save. Try refreshing the data.', 'error')
      return
    }

    if (!countryCode) return
    setIsSaving(true)
    try {
      const res = await mtInlineEditPrepareData(edits, screenName, countryCode)
      if ((res as any).success) {
        const result = (res as any).data
        // The backend may return HTTP 200 with success:true but a FAILED status
        // and per-row validationErrors. Surface those the same way regardless of
        // whether errorCount is present.
        // Map any server-side validation errors back onto the cells.
        const serverValidation = result?.validationErrors ?? []
        if (Array.isArray(serverValidation) && serverValidation.length > 0) {
          const serverErrors: Record<string, Record<string, boolean>> = {}
          let firstServerMsg: string | null = null
          serverValidation.forEach((v: any) => {
            const rowId = v.uuid
            const fieldKey = v.field
            const message = v.message || v.errorMessage
            if (rowId && fieldKey) {
              if (!serverErrors[rowId]) serverErrors[rowId] = {}
              serverErrors[rowId][fieldKey] = true
            }
            if (!firstServerMsg && message) {
              const rowIndex = data.findIndex(r => getRowId(r) === rowId)
              firstServerMsg =
                rowIndex >= 0 ? `Row ${rowIndex + 1}: ${message}` : message
            }
          })
          setFieldErrors(serverErrors)
          showSnackbar(
            firstServerMsg || result?.message || 'Some rows failed validation',
            'error'
          )
          // Keep edit mode open so the user can fix the flagged cells.
          return
        }

        // Some backends return HTTP 200 + success:true but a FAILED status /
        // errorCount without a populated validationErrors array. Treat that as
        // an error instead of a false "updated successfully".
        const status = String(result?.status ?? '').toUpperCase()
        if (
          (result?.errorCount ?? 0) > 0 ||
          (status && status !== 'SUCCESS' && status !== 'PARTIAL_SUCCESS')
        ) {
          showSnackbar(result?.message || 'Failed to update records', 'error')
          return
        }

        showSnackbar(
          result?.message ||
            `${result?.successCount ?? edits.length} row(s) updated successfully`,
          'success'
        )
        handleCancelEdit()
        await fetchData({ page: currentPage })
      } else {
        // Surface the real backend error text (falls back to a generic message).
        const errText = (res as any).error
        // eslint-disable-next-line no-console
        console.error('Inline edit failed:', res)
        showSnackbar(errText || 'Failed to update records', 'error')
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Inline edit threw:', err)
      showSnackbar(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to update records',
        'error'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleSuggestWHTRate = async () => {
    if (!filterData || !countryCode || isSuggestRateLoading) return
    const periodFilters = (filterData.months ?? []).map(
      m => `${m.year}${String(m.month).padStart(2, '0')}`
    )
    const entityTans = filterData.entityIdentifiers ?? []
    if (entityTans.length === 0 || periodFilters.length === 0) {
      showSnackbar('Select entity and period before suggesting rate', 'info')
      return
    }
    setIsSuggestRateLoading(true)
    try {
      const res = await mtCalculateRateRequest(
        { entityTans, periodFilters, uploadCategory: screenName },
        countryCode
      )
      if ((res as any).success) {
        showSnackbar(
          (res as any).data ||
            'Rate determination triggered for the selected period',
          'success'
        )
        // Rate determination just triggered — refresh button state so it
        // reflects the now-in-progress determination (typically disabled).
        await fetchRateDeterminationStatus(filterData, screenName)
      } else {
        showSnackbar((res as any).error || 'Suggest WHT Rate failed', 'error')
      }
    } catch {
      showSnackbar('Suggest WHT Rate failed', 'error')
    } finally {
      setIsSuggestRateLoading(false)
    }
  }

  const handleReprocess = async () => {
    if (isCooldown || !countryCode) return
    setIsReprocessLoading(true)
    setIsCooldown(true)
    if (cooldownRef.current) clearTimeout(cooldownRef.current)
    cooldownRef.current = setTimeout(() => setIsCooldown(false), 30000)
    try {
      const payload = {
        pageFilter: null,
        searchCOList: [],
        entityIdentifiers: filterData?.entityIdentifiers,
        periodFilters: filterData?.months,
        isReportDownload: false,
      }
      const res = await mtReprocessPrepare(payload, screenName, countryCode)
      if ((res as any).success) {
        showSnackbar('Reprocessing request has been initiated', 'success')
      } else {
        showSnackbar((res as any).error || 'Reprocess failed', 'error')
      }
    } catch {
      showSnackbar('Reprocess failed', 'error')
    } finally {
      setIsReprocessLoading(false)
    }
  }

  const handleDownload = async () => {
    if (totalItems === 0 || !countryCode) {
      showSnackbar('No Data Found', 'info')
      return
    }
    showSnackbar('Request Initiated', 'info')
    try {
      const payload = {
        pageFilter: {
          currentPage: 1,
          pageSize: 100,
          sortField: 'uuid',
          sortOrder: 'DESC',
          includeCount: false,
        },
        searchCOList: [],
        entityIdentifiers: filterData?.entityIdentifiers,
        periodFilters: filterData?.months,
        isReportDownload: true,
      }
      const res = await mtExportPrepareData(payload, screenName, countryCode)
      if ((res as any).success) {
        // Export API returns the generated report id, e.g. { data: 712 }.
        const generatedId = (res as any).data
        showSnackbar(
          `Download request generated successfully. Reference ID: ${generatedId}`,
          'success'
        )
      } else {
        showSnackbar((res as any).error || 'Download failed', 'error')
      }
    } catch {
      showSnackbar('Download failed', 'error')
    }
  }

  // ── Tab label with count badge ──
  const tabLabel = (tab: (typeof MT_PREPARE_TABS)[number]) => {
    const count = tabCounts[tab.screenName] ?? 0
    return `${tab.label} (${count})`
  }

  // ── Empty message ──
  const emptyMessage = useMemo(() => {
    if (screenName === 'NEGATIVE') {
      const sub = currentTab.children?.find(c => c.id === selectedSubTab)
      return `No ${sub?.label.toLowerCase() || 'negative'} transactions found`
    }
    return `No ${currentTab.label.toLowerCase()} transactions found`
  }, [screenName, currentTab, selectedSubTab])

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* AC4 – Key Filters: hide in full screen mode */}
      {!isFullScreen && (
        <MTKeyFilterPanel onViewClick={handleView} showYear showMonth />
      )}

      {showTable && (
        <>
          {/* Toolbar */}
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              px: 3,
              py: 1,
              minHeight: '48px !important',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              {`Total Transactions (${totalTransactions})`}
            </Typography>

            {/* AC10 – Toolbar icons per bucket */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {toolbarConfig.fullScreen &&
                (isFullScreen ? (
                  <ActionIconBtn
                    title="Exit Full Screen"
                    icon={<FullScreenIcon />}
                    onClick={handleFullScreenExit}
                  />
                ) : (
                  <ActionIconBtn
                    title="Full Screen"
                    icon={<FullScreenIcon />}
                    onClick={handleFullscreen}
                  />
                ))}

              {toolbarConfig.columnConfig && (
                <ActionIconBtn
                  title="Column Configuration"
                  icon={<ColumnConfigurationIcon />}
                  onClick={handleColumnConfigClick}
                />
              )}

              {/* Bulk (inline) edit controls — same Edit/Save/Cancel buttons as Service Master */}
              {toolbarConfig.edit && !editMode && (
                <Tooltip
                  title={
                    editLocked
                      ? 'Another operation (rate determination / edit) is in progress. Please wait until it completes.'
                      : 'Edit'
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleEditClick}
                      aria-label="Edit"
                      disabled={totalItems === 0 || editLocked}
                      sx={TOOLBAR_ICON_SX}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {toolbarConfig.edit && editMode && (
                <>
                  <Tooltip title="Save">
                    <span>
                      <IconButton
                        onClick={handleSaveEdit}
                        aria-label="Save"
                        disabled={isSaving}
                        sx={TOOLBAR_ICON_SX}
                      >
                        <SaveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <span>
                      <IconButton
                        onClick={handleCancelEdit}
                        aria-label="Cancel"
                        disabled={isSaving}
                        sx={TOOLBAR_ICON_SX}
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}

              {toolbarConfig.excludeInclude && (
                <ActionIconBtn
                  title={
                    screenName === 'PROCESSED'
                      ? 'Exclude all/selected records'
                      : 'Include all/selected records'
                  }
                  icon={<FileExcludeIcon />}
                  onClick={handleExcludeInclude}
                  disabled={selectedRows.length === 0}
                />
              )}

              {toolbarConfig.download && (
                <ActionIconBtn
                  title="Download Excel"
                  icon={<DownloadIcon />}
                  onClick={handleDownload}
                />
              )}

              {toolbarConfig.search && (
                <ActionIconBtn
                  title="Search"
                  icon={<SearchIconBtn />}
                  onClick={handleSearch}
                />
              )}

              {/* Action buttons */}
              {toolbarConfig.suggestWHTRate && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 16, margin: '9px 4px 0 2px' }}
                  />
                  <Tooltip
                    title={
                      suggestRateLocked
                        ? 'Another operation (rate determination / edit) is in progress. Please wait until it completes.'
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        disabled={
                          isSuggestRateLoading ||
                          !suggestRateButtonEnabled ||
                          suggestRateLocked
                        }
                        onClick={handleSuggestWHTRate}
                        sx={{
                          px: '16px',
                          py: '8px',
                          height: '32px',
                          textTransform: 'none',
                          fontWeight: 500,
                          marginTop: '1px',
                          backgroundColor: 'var(--forbrand-bg-variant)',
                          color: 'var(--forbrand-brand-varient)',
                        }}
                      >
                        Suggest WHT Rate
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}

              {toolbarConfig.reprocess && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 16, margin: '9px 4px 0 2px' }}
                  />
                  <Button
                    variant="contained"
                    disabled={isReprocessLoading || isCooldown}
                    onClick={handleReprocess}
                    sx={{
                      px: '16px',
                      py: '8px',
                      height: '32px',
                      textTransform: 'none',
                      fontWeight: 500,
                      marginTop: '1px',
                      backgroundColor: 'var(--forbrand-bg-variant)',
                      color: 'var(--forbrand-brand-varient)',
                    }}
                  >
                    Reprocess
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>

          {/* AC2 – Five sub-tabs with AC3 count badges */}
          <Box>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                width: '100%',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '14px',
                  minWidth: 0,
                  maxWidth: 'none',
                  flex: 1,
                  padding: '12px 16px',
                  background: 'var(--bg-layer-1)',
                  borderBottom: '2px solid var(--forbrand-secondary)',
                  color: 'var(--forbrand-secondary)',
                  '&.Mui-selected': {
                    color: 'var(--forbrand-primary)',
                    fontWeight: 700,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'var(--forbrand-color)',
                  height: 4,
                },
                '.MuiTabs-list, & .MuiTabs-flexContainer': {
                  display: 'grid',
                  gridTemplateColumns: `repeat(${MT_PREPARE_TABS.length}, 1fr)`,
                },
              }}
            >
              {MT_PREPARE_TABS.map(tab => (
                <Tab
                  key={tab.screenName}
                  label={tabLabel(tab)}
                  sx={{
                    textTransform: 'none',
                    '&.Mui-selected': { color: 'var(--forbrand-color)' },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Negative Entries sub-tabs (Unmapped / Mapped) */}
          {selectedTab === 4 && currentTab.children && (
            <Box
              sx={{
                display: 'flex',
                borderLeft: '1px solid var(--border-subtel)',
                borderRight: '1px solid var(--border-subtel)',
              }}
            >
              {currentTab.children.map(sub => (
                <Box
                  key={sub.id}
                  onClick={() => handleSubTabChange(sub.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'var(--forbrand-primary)',
                    borderBottom:
                      selectedSubTab === sub.id
                        ? '2px solid var(--forbrand-color)'
                        : 'none',
                    backgroundColor:
                      selectedSubTab === sub.id ? '#ffffff' : 'transparent',
                    fontWeight: selectedSubTab === sub.id ? 700 : 400,
                  }}
                >
                  {sub.label}
                </Box>
              ))}
            </Box>
          )}

          {/* Data Table */}
          <Box ref={tableContainerRef}>
            <MTCommonTable
              columns={filteredColumns as any}
              data={data}
              loading={loading}
              showCheckbox={visibleColumns.length > 0 && !editMode}
              showFooter
              totals={editMode ? null : totalsRow}
              totalsLabel=""
              emptyMessage={emptyMessage}
              selectedItems={selectedRows}
              onSelectItem={handleSelectItem}
              onSelectAll={handleSelectAll}
              editMode={editMode}
              editingRowId={null}
              editingData={editingData}
              onFieldChange={handleFieldChange}
              fieldErrors={fieldErrors}
              rawMetadataFields={rawMetadataFields}
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        </>
      )}

      {/* Column Configuration popover */}
      {React.createElement(ColumnConfiguration as any, {
        columns,
        visibleColumns,
        onColumnChange: (cols: string[]) => setVisibleColumns(cols),
        open: columnConfigOpen,
        onClose: handleColumnConfigClose,
        anchorEl: columnConfigAnchor,
      })}

      {/* Advanced Search (AC5) */}
      {React.createElement(Search as any, {
        open: searchOpen,
        onClose: handleCloseSearch,
        onApply: handleApplySearch,
        onReset: handleResetSearch,
        anchorEl,
        fields: searchFields,
        placement: 'bottom-start',
        setSearchCriteria,
        searchCriteria,
      })}

      {unsavedChangesDialog}
    </Box>
  )
}

export default MTPrepare
